import { FC, useEffect, useMemo, useState } from "react";
import { FormikErrors, FormikProps } from "formik";

import {
  ArrowDownIcon,
  ArrowUpIcon,
  InfoFillIcon,
  InfoIcon,
  InvoiceIcon,
  TrashIcon,
} from "../../../assets/icons";
import { DatePicker, Divider, InputField } from "../../../components";
import { roundTo, formatAmount } from "../../../utils";
import { DrawdownFields } from "../../LoanCalculator/service";
import { useLoanDashboard } from "../../../contexts";
import { LoanProductType } from "../../../hooks/api/types";
import DeleteInvoiceModal from "./DeleteInvoiceModal";
import SummaryReadOnlyField from "./SummaryReadOnlyField";
import { toDateOrUndefined } from "./service";
import { DEFAULT_CURRENCY } from "./constants";

type InvoicesListProps = {
  availableLimit: number;
  maxDiscountLimit: number;
  formik: FormikProps<DrawdownFields>;
};

export const InvoicesList: FC<InvoicesListProps> = ({
  availableLimit,
  maxDiscountLimit,
  formik,
}) => {
  const {
    values,
    errors,
    touched,
    getFieldProps,
    setFieldTouched,
    setFieldValue,
  } = formik;
  const {
    state: { currentLoanApplication },
  } = useLoanDashboard();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [invoiceIndexToDelete, setInvoiceIndexToDelete] = useState<
    number | null
  >(null);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<number>>(
    new Set([0])
  );

  const totalAmount = useMemo(() => {
    return values.invoices.reduce(
      (sum, inv) => sum + Number(inv.requiredAmount || 0),
      0
    );
  }, [values?.invoices]);

  const completedInvoicesCount = useMemo(() => {
    return values.invoices.filter((invoice) =>
      Object.values(invoice).every(
        (v) => v !== "" && v !== null && v !== undefined
      )
    ).length;
  }, [values.invoices]);

  const currencyCode =
    values.currency || currentLoanApplication?.creditLine?.currency || "AED";

  const createEmptyInvoice = () => ({
    invoiceNumber: "",
    invoiceAmount: undefined,
    requiredAmount: undefined,
    issuedDate: undefined,
    dueDate: undefined,
    selected: false,
  });

  const handleAddInvoice = () => {
    if (values.invoices.length >= 10) {
      formik.setErrors({
        ...errors,
        invoices: "You cannot add more than 10 invoices.",
      });
      return;
    }

    const newIndex = values.invoices.length;
    setFieldValue("invoices", [...values.invoices, createEmptyInvoice()]);
    setExpandedInvoices((prev) => new Set(prev).add(newIndex));
  };

  const markInvoiceSectionAsTouched = (index: number) => {
    setFieldTouched(`invoices.${index}.invoiceNumber`, true);
    setFieldTouched(`invoices.${index}.invoiceAmount`, true);
    setFieldTouched(`invoices.${index}.requiredAmount`, true);
    setFieldTouched(`invoices.${index}.issuedDate`, true);
    setFieldTouched(`invoices.${index}.dueDate`, true);
  };

  const handleInvoiceAmountChange = (index: number, value: string) => {
    setFieldValue(`invoices.${index}.invoiceAmount`, value);

    if (!value) {
      setFieldValue(`invoices.${index}.requiredAmount`, "");
      return;
    }

    const numericAmount = Number(value);
    if (Number.isNaN(numericAmount)) return;

    if (
      currentLoanApplication?.loanType === LoanProductType.LOC_PAYABLE_FINANCING
    ) {
      // For payable financing: copy invoice amount to requested amount
      const target = Math.min(numericAmount, availableLimit);
      setFieldValue(`invoices.${index}.requiredAmount`, target);
    } else if (
      currentLoanApplication?.loanType ===
      LoanProductType.LOC_INVOICE_DISCOUNTING
    ) {
      // For invoice discounting: calculate as maxDiscountLimit% of invoice amount
      if (maxDiscountLimit !== undefined) {
        const calculatedAmount = (numericAmount * maxDiscountLimit) / 100;
        const rounded = roundTo(calculatedAmount, 3);
        const target = Math.min(rounded, availableLimit);
        setFieldValue(`invoices.${index}.requiredAmount`, target);
      }
    }
  };

  const handleDeleteRequest = (index: number) => {
    setInvoiceIndexToDelete(index);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (invoiceIndexToDelete === null) return;
    const updatedInvoices = values.invoices.filter(
      (_invoice, idx) => idx !== invoiceIndexToDelete
    );
    setFieldValue(
      "invoices",
      updatedInvoices.length ? updatedInvoices : [createEmptyInvoice()]
    );
    setInvoiceIndexToDelete(null);
  };

  const handleModalClose = () => {
    setIsDeleteModalOpen(false);
    setInvoiceIndexToDelete(null);
  };

  const toggleInvoiceExpansion = (index: number) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedInvoices(newExpanded);
  };

  const totalAmountLabel = useMemo(() => {
    return `Total drawdown amount <${currencyCode}>`;
  }, [currencyCode]);

  useEffect(() => {
    setFieldValue("totalAmount", totalAmount);
  }, [totalAmount]);

  return (
    <div className="cx-bg-white cx-rounded-2xl cx-p-4 sm:cx-p-6 cx-mb-6 cx-shadow-base">
      <div className="cx-flex cx-flex-col md:cx-flex-row md:cx-items-center cx-justify-between cx-gap-2 cx-mb-4">
        <h2 className="cx-text-xl cx-font-semibold cx-text-gray-900">
          List of invoices
        </h2>
      </div>
      {(currentLoanApplication?.loanProduct.type as LoanProductType) ===
        LoanProductType.LOC_INVOICE_DISCOUNTING &&
        maxDiscountLimit && (
          <div className="cx-flex cx-items-center cx-gap-2 cx-bg-[#EDF5FF] cx-rounded-lg cx-p-3 cx-mb-5 cx-text-[#2563EB]">
            <div className="cx-bg-white cx-rounded-full">
              <InfoFillIcon
                width={16}
                height={16}
                fill="currentColor"
                className="cx-text-inherit"
              />
            </div>
            <p className="cx-text-sm cx-text-gray-600">
              We will provide discount on invoice maximum of{" "}
              <span>{maxDiscountLimit}%</span> .
            </p>
          </div>
        )}
      {values.invoices.map((_invoice: any, index: number) => {
        const invoiceTouched = touched.invoices?.[index] || {};
        const invoiceErrors = errors.invoices
          ? typeof errors.invoices === "string"
            ? {}
            : (errors.invoices as FormikErrors<typeof values.invoices>)?.[
                index
              ] || {}
          : {};
        const isExpanded = expandedInvoices.has(index);
        const isInvoiceSectionTouched =
          invoiceTouched.invoiceNumber ||
          invoiceTouched.invoiceAmount ||
          invoiceTouched.requiredAmount ||
          invoiceTouched.issuedDate ||
          invoiceTouched.dueDate;

        return (
          <section
            key={index}
            className="cx-rounded-xl cx-border cx-border-gray-200 cx-mb-4  cx-shadow-base"
          >
            <div className="cx-flex cx-justify-between cx-items-center cx-bg-[#FAFAFA] cx-p-3  cx-rounded-t-xl">
              <button
                type="button"
                onClick={() => toggleInvoiceExpansion(index)}
                className="cx-flex cx-items-center cx-gap-2 cx-flex-1 cx-text-left"
              >
                <InvoiceIcon className="cx-w-6 cx-h-6 cx-text-[#2563EB]" />
                <h3 className="cx-text-base">Invoice details</h3>
              </button>
              <div className="cx-flex cx-items-center cx-gap-6">
                <button
                  type="button"
                  onClick={() => handleDeleteRequest(index)}
                  className="cx-inline-flex cx-items-center cx-gap-2 cx-text-sm cx-font-semibold cx-text-[#A2191F] cx-bg-white cx-border cx-border-gray-300 cx-rounded-full cx-px-4 cx-py-1.5 hover:cx-bg-[#FFF5F5]"
                >
                  <TrashIcon className="cx-w-4 cx-h-4" />
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => toggleInvoiceExpansion(index)}
                  className="cx-text-gray-500 hover:cx-text-gray-700"
                >
                  {isExpanded ? (
                    <ArrowUpIcon className="cx-w-4 cx-h-4" />
                  ) : (
                    <ArrowDownIcon className="cx-w-4 cx-h-4" />
                  )}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="cx-mt-5 cx-p-3 cx-mb-4">
                <div className="cx-grid cx-gap-4 lg:cx-grid-cols-3">
                  <div className="cx-relative cx-w-full">
                    <InputField
                      label="Invoice no."
                      inputProps={{
                        ...getFieldProps(`invoices.${index}.invoiceNumber`),
                        type: "text",
                        onChange: (e) => {
                          const sanitized = e.target.value.replace(/\s+/g, "");
                          setFieldValue(
                            `invoices.${index}.invoiceNumber`,
                            sanitized
                          );
                        },
                        onBlur: () => {
                          markInvoiceSectionAsTouched(index);
                        },
                      }}
                      error={
                        isInvoiceSectionTouched && invoiceErrors.invoiceNumber
                          ? String(invoiceErrors.invoiceNumber)
                          : ""
                      }
                      tick={
                        invoiceTouched.invoiceNumber &&
                        !invoiceErrors.invoiceNumber
                      }
                      className="cx-placeholder:text-medium cx-placeholder:text-gray-400 cx-border-[#E3E7ED] cx-rounded-lg cx-bg-white"
                      inputClassName="cx-rounded-lg"
                    />
                  </div>

                  <div className="cx-relative cx-w-full">
                    <InputField
                      label={`Invoice amount`}
                      inputProps={{
                        ...getFieldProps(`invoices.${index}.invoiceAmount`),
                        type: "text",
                        onChange: (e) =>
                          handleInvoiceAmountChange(index, e.target.value),
                        onBlur: () => markInvoiceSectionAsTouched(index),
                      }}
                      error={
                        isInvoiceSectionTouched && invoiceErrors.invoiceAmount
                          ? String(invoiceErrors.invoiceAmount)
                          : ""
                      }
                      tick={
                        invoiceTouched.invoiceAmount &&
                        !invoiceErrors.invoiceAmount
                      }
                      className="cx-placeholder:text-medium cx-placeholder:text-gray-400 cx-border-[#E3E7ED] cx-rounded-lg cx-bg-white"
                      inputClassName="cx-rounded-lg"
                      prepend={
                        values.invoices[index].invoiceAmount
                          ? currencyCode
                          : undefined
                      }
                      prependMarginTop={true}
                    />
                  </div>

                  <div className="cx-relative cx-w-full">
                    <InputField
                      label={`Requested amount`}
                      inputProps={{
                        ...getFieldProps(`invoices.${index}.requiredAmount`),
                        type: "text",
                        onBlur: () => {
                          markInvoiceSectionAsTouched(index);
                        },
                      }}
                      error={
                        isInvoiceSectionTouched && invoiceErrors.requiredAmount
                          ? String(invoiceErrors.requiredAmount)
                          : ""
                      }
                      tick={
                        invoiceTouched.requiredAmount &&
                        !invoiceErrors.requiredAmount
                      }
                      className="cx-placeholder:text-medium cx-placeholder:text-gray-400 cx-border-[#E3E7ED] cx-rounded-lg cx-bg-white"
                      inputClassName="cx-rounded-lg"
                      prepend={
                        values.invoices[index].requiredAmount
                          ? currencyCode
                          : undefined
                      }
                      prependMarginTop={true}
                    />
                  </div>
                </div>

                <div className="cx-grid cx-gap-4 lg:cx-grid-cols-3 cx-mt-4">
                  <div className="cx-relative cx-w-full">
                    <DatePicker
                      selectedDate={toDateOrUndefined(
                        values.invoices[index].issuedDate
                      )}
                      onChange={(date) =>
                        setFieldValue(`invoices.${index}.issuedDate`, date)
                      }
                      onBlur={() => {
                        markInvoiceSectionAsTouched(index);
                      }}
                      label="Issued date"
                      showDatePickerIcon={false}
                      showCalenderIcon={true}
                      dateFormat="dd MMMM, yyyy"
                      textAlignment="left"
                      max={new Date()}
                      tick={
                        invoiceTouched.issuedDate && !invoiceErrors.issuedDate
                      }
                      error={
                        isInvoiceSectionTouched && invoiceErrors.issuedDate
                          ? String(invoiceErrors.issuedDate)
                          : ""
                      }
                      className="cx-border-[#E3E7ED] cx-rounded-lg cx-bg-white cx-placeholder:text-medium cx-placeholder:text-gray-400"
                    />
                  </div>

                  <div className="cx-relative cx-w-full">
                    <DatePicker
                      selectedDate={toDateOrUndefined(
                        values.invoices[index].dueDate
                      )}
                      onChange={(date) =>
                        setFieldValue(`invoices.${index}.dueDate`, date)
                      }
                      onBlur={() => {
                        markInvoiceSectionAsTouched(index);
                      }}
                      label="Due date"
                      showDatePickerIcon={false}
                      showCalenderIcon={true}
                      dateFormat="dd MMMM, yyyy"
                      textAlignment="left"
                      min={new Date()}
                      tick={invoiceTouched.dueDate && !invoiceErrors.dueDate}
                      error={
                        isInvoiceSectionTouched && invoiceErrors.dueDate
                          ? String(invoiceErrors.dueDate)
                          : ""
                      }
                      className="cx-border-[#E3E7ED] cx-rounded-lg cx-bg-white cx-placeholder:text-medium cx-placeholder:text-gray-400"
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
        );
      })}
      <button
        type="button"
        onClick={handleAddInvoice}
        className="cx-text-[#335CFF] cx-font-medium cx-flex cx-items-center cx-gap-2 cx-mb-6"
      >
        <span className="cx-text-lg">+</span> Add another invoice
      </button>
      {typeof errors.invoices === "string" && (
        <div className="cx-text-text-error cx-text-sm cx-mb-4">
          {errors.invoices}
        </div>
      )}
      <div className="cx-grid cx-grid-cols-1 md:cx-grid-cols-2 cx-gap-4">
        <SummaryReadOnlyField
          label={totalAmountLabel}
          inputProps={{
            ...getFieldProps(`totalAmount`),
            name: "totalAmount",
            onBlur: () => setFieldTouched("totalAmount"),
            value: totalAmount ? totalAmount.toLocaleString() : "",
          }}
          error={errors.totalAmount ? String(errors.totalAmount) : ""}
          tick={!errors.totalAmount && !!totalAmount}
          className="cx-border-gray-200 cx-rounded-lg"
          inputClassName="cx-bg-gray-100 cx-text-tertiary placeholder:cx-text-tertiary cx-rounded-lg"
        />
        <SummaryReadOnlyField
          label="Number of invoices"
          inputProps={{
            ...getFieldProps(`numberOfInvoices`),
            name: "numberOfInvoices",
            onBlur: () => setFieldTouched("numberOfInvoices"),
            value: completedInvoicesCount || "",
          }}
          error={touched.numberOfInvoices ? errors.numberOfInvoices : ""}
          tick={touched.numberOfInvoices && !errors.numberOfInvoices}
          className="cx-border-gray-200 cx-rounded-lg cx-text-text-tertiary"
          inputClassName="cx-bg-gray-100 cx-text-tertiary placeholder:cx-text-tertiary cx-rounded-lg"
        />
      </div>
      <Divider />

      {/* Tentative AED Amount (shown only for non-AED currencies) */}
      {currencyCode !== DEFAULT_CURRENCY &&
        values.requestedAmountInAED !== undefined && (
          <div className="cx-mt-6 cx-space-y-3">
            {/* Disclaimer for non-AED currencies */}
            <div className="cx-flex cx-items-start cx-gap-3 cx-rounded-lg cx-mt-1">
              <div className="cx-bg-white cx-rounded-full cx-flex-shrink-0 cx-text-brand-primary-regular cx-mt-1">
                <InfoIcon width={16} height={16} />
              </div>
              <p className="cx-text-sm cx-text-gray-700 cx-text-text-secondary">
                Tentative total drawdown amount is AED{" "}
                <span className="cx-font-semibold">
                  {Number.isFinite(Number(values.requestedAmountInAED))
                    ? formatAmount(Number(values.requestedAmountInAED))
                    : "0"}
                </span>
                . The final disbursed amount will be confirmed at the time of
                disbursement based on the applicable exchange rate.
              </p>
            </div>
          </div>
        )}
      <DeleteInvoiceModal
        isOpen={isDeleteModalOpen}
        onClose={handleModalClose}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default InvoicesList;
